import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useBulkUpdateSeeds } from '@/hooks/use-seeds';

export interface BulkUpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  categories: string[];
  onSuccess: () => void;
}

export function BulkUpdateDialog({
  open,
  onOpenChange,
  selectedIds,
  categories,
  onSuccess,
}: BulkUpdateDialogProps) {
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [newCategory, setNewCategory] = useState('');
  const [difficulty, setDifficulty] = useState<string | undefined>(undefined);
  const mutation = useBulkUpdateSeeds();

  const handleSubmit = async () => {
    const finalCategory = category === '__new__' ? newCategory.trim() : category;
    if (!finalCategory && !difficulty) {
      toast.error('Set at least one field to update');
      return;
    }
    try {
      const result = await mutation.mutateAsync({
        ids: selectedIds,
        problem_category: finalCategory || undefined,
        difficulty_level: difficulty ? Number(difficulty) : undefined,
      });
      toast.success(`Updated ${result.updated} seed${result.updated === 1 ? '' : 's'}`);
      setCategory(undefined);
      setNewCategory('');
      setDifficulty(undefined);
      onOpenChange(false);
      onSuccess();
    } catch (e) {
      toast.error(`Bulk update failed: ${(e as Error).message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Bulk update {selectedIds.length} seeds</DialogTitle>
          <DialogDescription>
            Leave a field blank to keep it unchanged. Changes apply to every selected seed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={category ?? ''} onValueChange={(v) => setCategory(v || undefined)}>
              <SelectTrigger>
                <SelectValue placeholder="Keep unchanged" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
                <SelectItem value="__new__">+ New category…</SelectItem>
              </SelectContent>
            </Select>
            {category === '__new__' && (
              <Input
                autoFocus
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="New category name"
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>Difficulty level</Label>
            <Select value={difficulty ?? ''} onValueChange={(v) => setDifficulty(v || undefined)}>
              <SelectTrigger>
                <SelectValue placeholder="Keep unchanged" />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    Level {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending}>
            {mutation.isPending ? 'Updating…' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
